import React, { useCallback, useMemo, useState } from 'react';
import {
  Bell,
  Briefcase,
  Calendar,
  Circle,
  Clipboard,
  Clock,
  Coffee,
  CreditCard,
  FileText,
  Gift,
  Home,
  Key,
  PieChart,
  RefreshCw,
  ShoppingBag,
  Star,
  Tag,
  Tool,
  Users,
  Zap,
} from 'react-feather';
import {
  getPath,
  ROUTE_CALENDARS,
  ROUTE_DETAILED_STATISTICS,
  ROUTE_COMPANY_CLIENTS,
  ROUTE_COMPANY_LEGAL_INFO,
  ROUTE_COMPANY_OVERVIEW,
  ROUTE_COMPANY_STATISTICS,
  ROUTE_COMPANY_SUBSCRIPTIONS,
  ROUTE_INTEGRATION_API,
  ROUTE_INTEGRATION_WEB_WIDGET,
  ROUTE_INVOICES,
  ROUTE_ONLINE_PAYMENTS,
  ROUTE_PRODUCTS,
  ROUTE_PROMO_CODES,
  ROUTE_RESERVATIONS,
  ROUTE_REVIEWS,
  ROUTE_SERVICES,
  ROUTE_USER_ROLES,
  ROUTE_VENUES,
  ROUTE_TICKET_STATISTICS,
  ROUTE_CAMPAIGNS,
  ROUTE_GIFT_CARDS,
  ROUTE_TRANSACTIONS_STATISTICS,
  ROUTE_GIFT_CARD_USES,
  ROUTE_NOTIFICATIONS,
  ROUTE_RESERVATION_SCANNER_KIOSK,
  ROUTE_GIFT_CARD_STATISTICS,
  ROUTE_ACTIVITY_LOGS,
} from '@src/@data/router/consts';
import { useMessages } from '@hooks/useMessages';
import { useCompanies } from '@src/@data/store/CompaniesProvider';
import { useCompany } from '@src/@data/store/CompanyProvider';
import VerticalNavMenuLink from './VerticalNavMenuLink';
import VerticalNavMenuSectionHeader from './VerticalNavMenuSectionHeader';
import { findCompany } from '@src/@data/utility/companies';
import { MenuBadge } from '@src/components/layouts/components/menu/vertical-menu/MenuBadge';
import { useFeatureFlags } from '@data/store/FeatureFlagsProvider';
import { useAreTicketsAvailable } from '@src/@data/utility/hooks/useAreTicketsAvailable';
import { useAuth } from '@src/@data/store/AuthProvider';
import VerticalNavMenuGroup from './VerticalNavMenuGroup';

enum ExpandableMenuGroups {
  GiftCards,
}

const VerticalMenuNavItems: React.FC = () => {
  const { messages } = useMessages();
  const { selectedCompanyId } = useCompany();
  const { companies } = useCompanies();
  const { isAdminBooklaUser } = useAuth();
  const {
    areProductsAllowed,
    areStatisticsAllowed,
    areFinancesAllowed,
    areIntegrationsAllowed,
    isAccessManagementAllowed,
    isCompanyEditingAllowed,
    areSMSCampaignsEnabled,
    areActivityLogsAllowed,
  } = useFeatureFlags();
  const areTicketsAvailable = useAreTicketsAvailable();
  const [expandedItemId, setExpandedItemId] = useState<ExpandableMenuGroups>();

  const hasOnlinePayments = useMemo<boolean>(() => {
    return findCompany(companies, selectedCompanyId)?.stripeEnabled || false;
  }, [companies, selectedCompanyId]);

  const requiresStripeInfo = useMemo<boolean>(() => {
    return (
      findCompany(companies, selectedCompanyId)?.stripeInfoRequired || false
    );
  }, [companies, selectedCompanyId]);

  const paymentsBadge = useMemo<MenuBadge | undefined>(() => {
    return requiresStripeInfo ? { color: 'warning', text: '!' } : undefined;
  }, [requiresStripeInfo]);

  const createToggleExpandHandler = useCallback(
    (id: ExpandableMenuGroups) => () => {
      setExpandedItemId((prev) => (prev === id ? undefined : id));
    },
    []
  );

  if (!selectedCompanyId) return null;

  return (
    <>
      <VerticalNavMenuLink
        title={messages('nav_menu_about')}
        icon={Briefcase}
        navLink={getPath(ROUTE_COMPANY_OVERVIEW, {
          companyId: selectedCompanyId,
        })}
      />
      <VerticalNavMenuLink
        key="clients"
        title={messages('nav_menu_clients')}
        icon={Users}
        navLink={getPath(ROUTE_COMPANY_CLIENTS, {
          companyId: selectedCompanyId,
        })}
      />
      <VerticalNavMenuLink
        title={messages('nav_menu_calendars')}
        icon={Calendar}
        navLink={getPath(ROUTE_CALENDARS, { companyId: selectedCompanyId })}
      />
      <VerticalNavMenuLink
        title={messages('nav_menu_services')}
        icon={Coffee}
        navLink={getPath(ROUTE_SERVICES, { companyId: selectedCompanyId })}
      />
      <VerticalNavMenuLink
        title={messages('nav_menu_promo_codes')}
        icon={Tag}
        navLink={getPath(ROUTE_PROMO_CODES, { companyId: selectedCompanyId })}
      />
      {areProductsAllowed && (
        <>
          <VerticalNavMenuLink
            key="products"
            title={messages('nav_menu_products')}
            icon={ShoppingBag}
            navLink={getPath(ROUTE_PRODUCTS, { companyId: selectedCompanyId })}
          />
          <VerticalNavMenuGroup
            title={messages('nav_menu_gift_cards')}
            icon={Gift}
            isExpanded={expandedItemId === ExpandableMenuGroups.GiftCards}
            toggle={createToggleExpandHandler(ExpandableMenuGroups.GiftCards)}
          >
            <VerticalNavMenuLink
              title={messages('nav_menu_gift_cards_edit')}
              icon={Circle}
              navLink={getPath(ROUTE_GIFT_CARDS, {
                companyId: selectedCompanyId,
              })}
            />
            <VerticalNavMenuLink
              title={messages('nav_menu_gift_card_uses')}
              icon={Circle}
              navLink={getPath(ROUTE_GIFT_CARD_USES, {
                companyId: selectedCompanyId,
              })}
            />
            <VerticalNavMenuLink
              title={messages('nav_menu_gift_card_statistics')}
              icon={Circle}
              navLink={getPath(ROUTE_GIFT_CARD_STATISTICS, {
                companyId: selectedCompanyId,
              })}
            />
          </VerticalNavMenuGroup>
        </>
      )}
      <VerticalNavMenuLink
        title={messages('nav_menu_reservations')}
        icon={Clock}
        navLink={getPath(ROUTE_RESERVATIONS, {
          companyId: selectedCompanyId,
        })}
      />
      <VerticalNavMenuLink
        title={messages('nav_menu_reviews')}
        icon={Star}
        navLink={getPath(ROUTE_REVIEWS, {
          companyId: selectedCompanyId,
        })}
      />
      {areStatisticsAllowed && (
        <>
          <VerticalNavMenuSectionHeader
            header={messages('nav_menu_statistics')}
          />
          <VerticalNavMenuLink
            title={messages('nav_menu_company_statistics')}
            icon={PieChart}
            navLink={getPath(ROUTE_COMPANY_STATISTICS, {
              companyId: selectedCompanyId,
            })}
          />
          <VerticalNavMenuLink
            title={messages('nav_menu_transactions_statistics')}
            icon={PieChart}
            navLink={getPath(ROUTE_TRANSACTIONS_STATISTICS, {
              companyId: selectedCompanyId,
            })}
          />
          <VerticalNavMenuLink
            title={messages('nav_menu_detailed_statistics')}
            icon={PieChart}
            navLink={getPath(ROUTE_DETAILED_STATISTICS, {
              companyId: selectedCompanyId,
            })}
          />
          {areTicketsAvailable && (
            <VerticalNavMenuLink
              title={messages('nav_menu_ticket_statistics')}
              icon={PieChart}
              navLink={getPath(ROUTE_TICKET_STATISTICS, {
                companyId: selectedCompanyId,
              })}
            />
          )}
        </>
      )}
      {areSMSCampaignsEnabled && (
        <>
          <VerticalNavMenuSectionHeader
            header={messages('nav_menu_marketing')}
          />
          <VerticalNavMenuLink
            title={messages('nav_menu_campaigns')}
            icon={Zap}
            navLink={getPath(ROUTE_CAMPAIGNS, {
              companyId: selectedCompanyId,
            })}
          />
        </>
      )}
      {isCompanyEditingAllowed && (
        <>
          <VerticalNavMenuSectionHeader header={messages('nav_menu_addons')} />
          <VerticalNavMenuLink
            title={messages('nav_menu_checkins')}
            icon={Home}
            navLink={getPath(ROUTE_VENUES, {
              companyId: selectedCompanyId,
            })}
          />
        </>
      )}
      {areFinancesAllowed && (
        <>
          <VerticalNavMenuSectionHeader header={messages('nav_menu_billing')} />
          <VerticalNavMenuLink
            title={messages('nav_menu_invoices')}
            icon={FileText}
            navLink={getPath(ROUTE_INVOICES, { companyId: selectedCompanyId })}
          />
          {hasOnlinePayments && (
            <VerticalNavMenuLink
              title={messages('nav_menu_online_payments')}
              icon={CreditCard}
              navLink={getPath(ROUTE_ONLINE_PAYMENTS, {
                companyId: selectedCompanyId,
              })}
              badge={paymentsBadge}
            />
          )}
          <VerticalNavMenuLink
            title={messages('nav_menu_subscriptions')}
            icon={RefreshCw}
            navLink={getPath(ROUTE_COMPANY_SUBSCRIPTIONS, {
              companyId: selectedCompanyId,
            })}
          />
        </>
      )}
      {areIntegrationsAllowed && (
        <>
          <VerticalNavMenuSectionHeader
            header={messages('nav_menu_integrations')}
          />
          <VerticalNavMenuLink
            title={messages('nav_menu_web_widget')}
            icon={Tool}
            navLink={getPath(ROUTE_INTEGRATION_WEB_WIDGET, {
              companyId: selectedCompanyId,
            })}
          />
          {isAdminBooklaUser && (
            <VerticalNavMenuLink
              title={messages('nav_menu_reservation_scanner_kiosk')}
              icon={Tool}
              navLink={getPath(ROUTE_RESERVATION_SCANNER_KIOSK, {
                companyId: selectedCompanyId,
              })}
            />
          )}
          <VerticalNavMenuLink
            title={messages('nav_menu_api_integration')}
            icon={Tool}
            navLink={getPath(ROUTE_INTEGRATION_API, {
              companyId: selectedCompanyId,
            })}
          />
        </>
      )}
      {(areFinancesAllowed || isAccessManagementAllowed) && (
        <VerticalNavMenuSectionHeader
          header={messages('nav_menu_company_settings')}
        />
      )}
      {areFinancesAllowed && (
        <VerticalNavMenuLink
          title={messages('nav_menu_legal_info')}
          icon={FileText}
          navLink={getPath(ROUTE_COMPANY_LEGAL_INFO, {
            companyId: selectedCompanyId,
          })}
        />
      )}
      {isAccessManagementAllowed && (
        <VerticalNavMenuLink
          title={messages('nav_menu_user_roles')}
          icon={Key}
          navLink={getPath(ROUTE_USER_ROLES, {
            companyId: selectedCompanyId,
          })}
        />
      )}
      {isCompanyEditingAllowed && (
        <VerticalNavMenuLink
          title={messages('nav_menu_notifications')}
          icon={Bell}
          navLink={getPath(ROUTE_NOTIFICATIONS, {
            companyId: selectedCompanyId,
          })}
        />
      )}
      {areActivityLogsAllowed && (
        <VerticalNavMenuLink
          title={messages('nav_menu_activity_logs')}
          icon={Clipboard}
          navLink={getPath(ROUTE_ACTIVITY_LOGS, {
            companyId: selectedCompanyId,
          })}
        />
      )}
    </>
  );
};

export default VerticalMenuNavItems;
